import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

import { BagPrintItem, PRINT_SELECTED_TAGS, PrintSelectedTagsResponse } from '../../bags/graphql/mutations/batchOperations';
import { printPdfFromBase64 } from '../../bags/utils/printUtils';
import { PrintJob } from '../types';

/** 打印份数：空 / 非法按 1 张计（与原型 labels 一致） */
const copiesOf = (job: PrintJob): number => Math.max(1, Math.floor(Number(job.copies) || 1));

/** 本地当前时间，服务端按此格式化打印时间（与 wms/bags PrintTagsModal 同一写法） */
const localTimeNow = (): string => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

/**
 * 包牌打印发送（原「打印份数确认」后原本还要跳一次「确认打印」二次确认弹窗，内容与份数弹窗完全重复，
 * 已去掉：份数确认后直接发送）：调用 printSelectedTags 生成标签 PDF，由浏览器弹窗打印
 * （与 wms/bags 的 PrintTagsModal 走同一条真实链路，不再本地渲染假标签）。
 */
export const usePrintSelectedTags = () => {
    const { t } = useTranslation();
    const [printSelectedTags, { loading }] = useMutation<PrintSelectedTagsResponse>(PRINT_SELECTED_TAGS);

    const sendToPrinter = async (jobs: PrintJob[]) => {
        if (jobs.some((j) => !j.bagId)) {
            message.error(t('wms.sortingBatch.print.preview.missingBagId'));
            return;
        }
        const total = jobs.reduce((sum, j) => sum + copiesOf(j), 0);
        // 透传 batchId（正在打印的那个批次）：后端据此取标签批次段，而非物理当前批次(is_current)
        const items: BagPrintItem[] = jobs.map((j) => ({ bagId: j.bagId as string, copies: copiesOf(j), batchId: j.batchId }));
        try {
            const result = await printSelectedTags({
                variables: { items, localTime: localTimeNow(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            });
            const data = result.data?.printSelectedTags;
            if (data?.success && data.pdfBase64) {
                printPdfFromBase64(data.pdfBase64, `batch_bag_tags_${Date.now()}.pdf`);
                message.success(t('wms.sortingBatch.print.preview.sent', { count: total }));
            } else {
                message.error(data?.message || t('wms.sortingBatch.print.preview.failed'));
            }
        } catch {
            message.error(t('wms.sortingBatch.print.preview.failed'));
        }
    };

    return { sendToPrinter, sending: loading };
};
